import moment from 'moment';

export const TIME_INCREMENT_MINUTES = 15;

/**
 * Checks if start and end times span multiple days.
 */
export function checkMultiDay(startTime: number, endTime: number): boolean {
    const startDate = moment.unix(startTime).format('DD/MM/YYYY');
    const endDate = moment.unix(endTime).format('DD/MM/YYYY');

    return startDate !== endDate;
}

/**
 * Combines the date portion from one timestamp with the time portion from another.
 * Useful when changing just the date or just the time of a datetime value.
 */
export function getTimeForSelectedDate(dateTimestamp: number, timeTimestamp: number): number {
    const date = moment.unix(dateTimestamp);
    const time = moment.unix(timeTimestamp);

    return date.set({hour: time.hour(), minute: time.minute(), second: 0, millisecond: 0}).unix();
}

/**
 * Gets a valid end time when the start time changes.
 * Maintains the duration between start and end times.
 */
export function getValidEndTime(
    newStartTime: number,
    endTime: number,
    timeDuration: number
): number {
    return moment.unix(newStartTime).add(timeDuration, 's').unix();
}

/**
 * Gets a valid start time when the end time changes.
 * If the new end time is before the current start time, adjusts the start time
 * to be 15 minutes before the end time (but not before start of day).
 */
export function getValidStartTime(startTime: number, newEndTime: number): number {
    const startMoment = moment.unix(startTime);
    const endMoment = moment.unix(newEndTime);

    if (endMoment.isBefore(startMoment)) {
        const newStartMoment = endMoment.clone().subtract(TIME_INCREMENT_MINUTES, 'm');
        const startOfDay = endMoment.clone().startOf('day');

        // Don't allow the start time to go before 12:00 AM of the same day
        if (newStartMoment.isBefore(startOfDay)) {
            return startOfDay.unix();
        }

        return newStartMoment.unix();
    }

    return startTime;
}

/**
 * Converts times to all-day format.
 * Start time becomes beginning of day (00:00).
 * End time becomes 23:45 on the end date.
 */
export function getTimesForAllDay(
    startTime: number,
    endTime: number
): {startTime: number; endTime: number} {
    return {
        startTime: moment.unix(startTime).startOf('day').unix(),
        endTime: moment.unix(endTime).hour(23).minute(45).second(0).unix(),
    };
}
